import { supabase } from '../lib/supabase'
import { logger } from '../utils/logger'

export interface ModelRating {
  id: string
  model_id: string
  client_id: string
  rating: number
  comment?: string
  created_at: string
  client_name?: string
}

export interface RatingStats {
  average: number
  count: number
  distribution: {
    5: number
    4: number
    3: number
    2: number
    1: number
  }
}

class RatingService {
  /**
   * Adiciona ou atualiza uma avaliação de modelo
   */
  async submitRating(
    modelId: string,
    clientId: string,
    rating: number,
    comment?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Validar rating
      if (rating < 1 || rating > 5) {
        return { success: false, error: 'Avaliação deve ser entre 1 e 5 estrelas' }
      }

      // Verificar se cliente já avaliou este modelo
      const { data: existingRating } = await supabase
        .from('model_ratings')
        .select('id')
        .eq('model_id', modelId)
        .eq('client_id', clientId)
        .maybeSingle()

      let result

      if (existingRating) {
        // Atualizar avaliação existente
        const { data, error } = await supabase
          .from('model_ratings')
          .update({
            rating,
            comment,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingRating.id)
          .select()
          .single()

        if (error) throw error
        result = data
      } else {
        // Inserir nova avaliação
        const { data, error } = await supabase
          .from('model_ratings')
          .insert({
            model_id: modelId,
            client_id: clientId,
            rating,
            comment
          })
          .select()
          .single()

        if (error) throw error
        result = data
      }

      logger.info('Rating submitted successfully', { 
        modelId, 
        clientId, 
        rating: result.rating,
        isUpdate: !!existingRating 
      })

      return { success: true }
    } catch (error: any) {
      logger.error('Error submitting rating', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Obtém todas as avaliações de um modelo
   */
  async getModelRatings(modelId: string): Promise<ModelRating[]> {
    try {
      const { data, error } = await supabase
        .from('model_ratings')
        .select(`
          *,
          clients(name)
        `)
        .eq('model_id', modelId)
        .order('created_at', { ascending: false })

      if (error) throw error

      return (data || []).map(rating => ({
        ...rating,
        client_name: rating.clients?.name || 'Cliente Anônimo'
      }))
    } catch (error) {
      logger.error('Error getting model ratings', error)
      return []
    }
  }

  /**
   * Obtém estatísticas detalhadas das avaliações de um modelo
   */
  async getModelRatingStats(modelId: string): Promise<RatingStats> {
    try {
      const { data, error } = await supabase
        .from('model_ratings')
        .select('rating')
        .eq('model_id', modelId)

      if (error) throw error

      const ratings = data || []
      const count = ratings.length

      if (count === 0) {
        return {
          average: 0,
          count: 0,
          distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
        }
      }

      // Calcular média
      const sum = ratings.reduce((acc, r) => acc + r.rating, 0)
      const average = Math.round((sum / count) * 100) / 100

      // Calcular distribuição
      const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
      ratings.forEach(r => {
        distribution[r.rating as keyof typeof distribution]++
      })

      return { average, count, distribution }
    } catch (error) {
      logger.error('Error getting model rating stats', error)
      return {
        average: 0,
        count: 0,
        distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
      }
    }
  }

  /**
   * Verifica se um cliente já avaliou um modelo
   */
  async hasClientRatedModel(modelId: string, clientId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('model_ratings')
        .select('id')
        .eq('model_id', modelId)
        .eq('client_id', clientId)
        .maybeSingle()

      if (error) throw error
      return !!data?.id
    } catch (error) {
      logger.error('Error checking if client rated model', error)
      return false
    }
  }

  /**
   * Obtém a avaliação que um cliente deu a um modelo
   */
  async getClientRatingForModel(modelId: string, clientId: string): Promise<ModelRating | null> {
    try {
      const { data, error } = await supabase
        .from('model_ratings')
        .select('*')
        .eq('model_id', modelId)
        .eq('client_id', clientId)
        .maybeSingle()

      if (error) throw error

      return data
    } catch (error) {
      logger.error('Error getting client rating for model', error)
      return null
    }
  }

  /**
   * Remove uma avaliação (apenas o cliente que avaliou pode remover)
   */
  async removeRating(modelId: string, clientId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('model_ratings')
        .delete()
        .eq('model_id', modelId)
        .eq('client_id', clientId)

      if (error) throw error

      logger.info('Rating removed successfully', { modelId, clientId })
      return { success: true }
    } catch (error: any) {
      logger.error('Error removing rating', error)
      return { success: false, error: error.message }
    }
  }
}

export const ratingService = new RatingService()
